import { useEffect, useRef } from 'react';

/**
 * Component that injects raw HTML into the document head
 * @param {string} htmlContent - Raw HTML string to inject into head
 */
const HeaderHtmlInjector = ({ htmlContent }) => {
  // Track injected elements to clean up later
  const injectedElementsRef = useRef([]);

  useEffect(() => {
    // Skip if no content
    if (!htmlContent) return;

    // Create temporary container to parse HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    
    // Find all elements that belong in head, separating meta/link elements from scripts
    const metaElements = tempContainer.querySelectorAll('meta, link, title');
    const scriptStyleElements = tempContainer.querySelectorAll('script, style');
    
    const head = document.head;
    const elementsToInject = [];
    
    // Process and inject SEO elements (meta, link, title) at the beginning of head
    metaElements.forEach(element => {
      const newElement = document.createElement(element.tagName);
      
      // Copy all attributes
      Array.from(element.attributes).forEach(attr => {
        newElement.setAttribute(attr.name, attr.value);
      });
      
      // Add tracking attribute
      newElement.setAttribute('data-injected-header', 'true');
      newElement.setAttribute('data-injected-type', 'meta');
      
      // Insert at the beginning of head
      head.insertBefore(newElement, head.firstChild);
      elementsToInject.push(newElement);
    });
    
    // Process and inject script/style elements at the end of head
    scriptStyleElements.forEach(element => {
      const newElement = document.createElement(element.tagName);
      
      // Copy all attributes
      Array.from(element.attributes).forEach(attr => {
        newElement.setAttribute(attr.name, attr.value);
      });
      
      // Copy content for script and style tags
      newElement.textContent = element.textContent;
      
      // Add tracking attribute
      newElement.setAttribute('data-injected-header', 'true');
      newElement.setAttribute('data-injected-type', 'script');
      
      // Insert at the end of head
      head.appendChild(newElement);
      elementsToInject.push(newElement);
    });
    
    // Store references to all injected elements for cleanup
    injectedElementsRef.current = elementsToInject;
    
    // Clean up function to remove injected elements
    return () => {
      injectedElementsRef.current.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
      injectedElementsRef.current = [];
    };
  }, [htmlContent]);

  // This component doesn't render anything
  return null;
};

export default HeaderHtmlInjector;
